import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { FormFieldTextBelow } from '@ds/components/forms/FormField'
import { Icon } from '@ds/index'
import { zodResolver } from '@hookform/resolvers/zod'
import cx from 'classnames'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { l, TextArea, TextInputV2 } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { getDefaultValues } from 'apps/aiAutomation/components/Behavior/components/BehaviorEditor/lib/getDefaultValues'
import {
  AiAutomationBehaviorActions,
  AiAutomationBehaviorSelectors,
} from 'apps/aiAutomation/store/behavior'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { AiBehaviorProfile } from 'shared/api/requests/aiBehavior/schemas'

import cm from './styles.module.css'

export interface BehaviorEditorRef {
  submit: () => Promise<AiBehaviorProfile | null>
}

interface BehaviorEditorProps {
  isReadMode?: boolean
}

export const BehaviorEditor = forwardRef<BehaviorEditorRef, BehaviorEditorProps>(
  ({ isReadMode = false }, ref) => {
    const dispatch = useAppDispatch()

    const currentProfile = useAppSelector(AiAutomationBehaviorSelectors.getCurrentProfile)
    const generatedProfile = useAppSelector(AiAutomationBehaviorSelectors.getGeneratedProfile)

    const { register, formState, handleSubmit, setValue } = useForm<AiBehaviorProfile>({
      defaultValues: {
        role: '',
        brand_voice: '',
        guardrails: '',
      },
      resolver: zodResolver(
        z.object({
          role: z
            .string()
            .min(1, l.translate('Enter role to continue'))
            .max(300, l.translate('Role explanation must be 300 characters or fewer')),
          brand_voice: z
            .string()
            .min(1, l.translate('Enter Brand Voice & Persona to continue'))
            .max(1500, l.translate('Brand Voice & Persona must be 1500 characters or fewer')),
          guardrails: z
            .string()
            .min(1, l.translate('Enter Guardrails & Constraints to continue'))
            .max(1500, l.translate('Guardrails & Constraints must be 1500 characters or fewer')),
        }),
      ),
    })

    useImperativeHandle(ref, () => ({
      submit: () =>
        new Promise((resolve) => {
          handleSubmit(resolve, () => resolve(null))()
        }),
    }))

    const initialValuesSet = useRef(false)

    useEffect(() => {
      if (!initialValuesSet.current) {
        const defaultValues = getDefaultValues(generatedProfile ?? currentProfile)
        const shouldDirty = Boolean(generatedProfile)

        setValue('role', defaultValues.role, { shouldDirty })
        setValue('brand_voice', defaultValues.brand_voice, { shouldDirty })
        setValue('guardrails', defaultValues.guardrails, { shouldDirty })

        initialValuesSet.current = true
      }
    }, [generatedProfile, currentProfile, setValue])

    const { ref: roleRef, ...roleProps } = register('role')

    useEffect(() => {
      dispatch(AiAutomationLayoutActions.confirmChanged({ show: formState.isDirty }))
    }, [formState.isDirty, dispatch])

    useEffect(() => {
      return () => {
        dispatch(AiAutomationBehaviorActions.generatedProfileReset())
      }
    }, [dispatch])

    return (
      <form className={cm.formWrapper}>
        <div
          className={cx('m-b-lg', generatedProfile !== null && cm.animated)}
          style={{ '--delay': '0ms' } as React.CSSProperties}
        >
          <div className={cm.inputLabel}>
            <Icon.Bot size={24} />

            <span id="behavior-role-label" className="text-subheading">
              {l.translate('AI Role')}
            </span>
          </div>

          <TextInputV2
            ariaLabelledBy="behavior-role-label"
            inputRef={roleRef}
            readOnly={isReadMode}
            placeholder={l.translate('Marketing Coach and Growth Strategist')}
            className={cx(isReadMode && cm.input__readOnly)}
            {...roleProps}
            errorText={formState.errors.role?.message}
            invalid={Boolean(formState.errors.role?.message)}
          />
        </div>

        <div
          className={cx('m-b-lg', generatedProfile !== null && cm.animated)}
          style={{ '--delay': '200ms' } as React.CSSProperties}
        >
          <div className={cm.inputLabel}>
            <Icon.MessageTemplates size={24} />

            <span id="behavior-brand-voice-label" className="text-subheading">
              {l.translate('Brand Voice & Persona')}
            </span>
          </div>

          <TextArea
            ariaLabelledBy="behavior-brand-voice-label"
            className={cx(isReadMode && cm.textarea__readOnly)}
            readOnly={isReadMode}
            placeholder={l.translate(
              'Your tone is professional, confident, and results-oriented. Use industry terminology (e.g., ROI, conversion rate, lead magnet) naturally. Keep your energy high and encouraging, but always ground your advice in data and proven strategies. Don’t use emojis.',
            )}
            minRows={1}
            maxRows={14}
            {...register('brand_voice')}
            invalid={Boolean(formState.errors.brand_voice?.message)}
          />

          <FormFieldTextBelow
            helpText={l.translate(
              'The tone, personality, and communication style AI uses in every conversation',
            )}
            errorText={formState.errors.brand_voice?.message}
            showError={Boolean(formState.errors.brand_voice?.message)}
            fieldClasses={{
              helpText: 'm-t-xs d-block text-right',
            }}
          />
        </div>

        <div
          className={cx(generatedProfile !== null && cm.animated)}
          style={{ '--delay': '400ms' } as React.CSSProperties}
        >
          <div className={cm.inputLabel}>
            <Icon.Stop size={24} />

            <span id="behavior-guardrails-label" className="text-subheading">
              {l.translate('Guardrails & Constraints')}
            </span>
          </div>

          <TextArea
            ariaLabelledBy="behavior-guardrails-label"
            className={cx(isReadMode && cm.textarea__readOnly)}
            readOnly={isReadMode}
            placeholder={l.translate(
              "You must never provide specific legal or financial advice regarding business structures or taxes. Do not guarantee specific revenue results or 'get rich quick' outcomes. Don’t share internal pricing until the user has been qualified through your discovery questions.",
            )}
            minRows={1}
            maxRows={14}
            {...register('guardrails')}
            invalid={Boolean(formState.errors.guardrails?.message)}
          />

          <FormFieldTextBelow
            helpText={l.translate('The rules and limits AI follows in every conversation')}
            errorText={formState.errors.guardrails?.message}
            showError={Boolean(formState.errors.guardrails?.message)}
            fieldClasses={{
              helpText: 'm-t-xs d-block text-right',
            }}
          />
        </div>
      </form>
    )
  },
)
